/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { mappingMatrixEntryPoint } from '@jira/assets-app__data-manager-mapping-matrix/src/mappingMatrixEntryPoint.entrypoint.tsx';
import { cleansingStatisticsEntryPoint } from '@jira/assets-app__data-manager-cleansing-statistics/src/cleansingStatisticsEntryPoint.entrypoint.tsx';
import { defaultAttributeMappingEntryPoint } from '@jira/assets-app__data-manager-default-attribute-mapping/src/SrcdefaultAttributeMappingEntryPoint.entrypoint.tsx';
import { dataDictionariesEntryPoint } from '@jira/assets-app__data-manager-data-dictionaries/src/dataDictionariesEntryPoint.entrypoint.tsx';
import { dataManagerAttributesEntryPoint } from '@jira/assets-app__data-manager-attributes/src/dataManagerAttributesEntryPoint.entrypoint.tsx';
import { dataSourcePrioritiesEntryPoint } from '@jira/assets-app__data-manager-object-classes-settings/src/dataSourcePrioritiesEntryPoint.entrypoint.tsx';
import { attributePrioritiesEntryPoint } from '@jira/assets-app__data-manager-object-classes-settings/src/attributePrioritiesEntryPoint.entrypoint.tsx';
import type {
	ObjectClassSettingsEntryPoints,
	ObjectClassSettingsType,
} from '@jira/assets-app__data-manager-common/src/ObjectListViewTypes.types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

type ExtraProps = {
	settingType?: ObjectClassSettingsType;
	objectClassId?: string;
};

const SETTING_TYPES: readonly ObjectClassSettingsType[] = [
	'mapping-matrix',
	'default-attribute-mapping',
	'cleansing-statistics',
	'data-dictionaries',
	'attributes',
	'data-source-priorities',
	'attribute-priorities',
] as const;

const SETTINGS: Partial<Record<ObjectClassSettingsType, ReturnType<typeof createEntryPoint>>> = {
	'mapping-matrix': mappingMatrixEntryPoint,
	'default-attribute-mapping': defaultAttributeMappingEntryPoint,
	'cleansing-statistics': cleansingStatisticsEntryPoint,
	'data-dictionaries': dataDictionariesEntryPoint,
	attributes: dataManagerAttributesEntryPoint,
	'data-source-priorities': dataSourcePrioritiesEntryPoint,
	'attribute-priorities': attributePrioritiesEntryPoint,
};

const isValidSettingType = (settingType: unknown): settingType is ObjectClassSettingsType => {
	if (typeof settingType !== 'string') {
		return false;
	}
	return SETTING_TYPES.some((validType) => validType === settingType);
};

const getEntryPoint = (
	settingType: ObjectClassSettingsType,
): typeof mappingMatrixEntryPoint | undefined => {
	if (
		!fg('assets_data_manager_fast_follow') &&
		(settingType === 'data-source-priorities' || settingType === 'attribute-priorities')
	) {
		return undefined;
	}

	return SETTINGS[settingType];
};

type AssetsDataManagerObjectClassSettingsEntryPointComponent = EntryPointComponent<
	{},
	ObjectClassSettingsEntryPoints,
	{},
	ExtraProps
>;

export const assetsDataManagerObjectClassSettingsEntryPoint: EntryPoint<
	AssetsDataManagerObjectClassSettingsEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForInteraction<AssetsDataManagerObjectClassSettingsEntryPointComponent>(
		/* webpackChunkName: "async-assets-data-manager-object-class-settings", jiraSpaEntry: "async-assets-data-manager-object-class-settings" */ './AssetsDataManagerObjectClassSettingsPageSpa.tsx',
	),
	getPreloadProps: ({
		context: {
			match: { params },
		},
	}: EntryPointRouteParams) => {
		const validSettingType = isValidSettingType(params?.settingType)
			? params.settingType
			: undefined;
		const objectClassId = params?.objectClassId || undefined;
		if (validSettingType) {
			const entryPoint = getEntryPoint(validSettingType);
			if (entryPoint) {
				return {
					entryPoints: {
						[validSettingType]: {
							entryPoint,
							entryPointParams: {},
						},
					},
					extraProps: {
						settingType: validSettingType,
						objectClassId,
					},
				};
			}
		}

		return {
			entryPoints: {},
			extraProps: {
				settingType: validSettingType,
				objectClassId,
			},
		};
	},
});
